import { WebPageDataService } from "VSS/Contributions/Services";
import { getService } from "VSS/Service";
import { ILinkedArtifact } from "TFS/WorkItemTracking/ExtensionContracts";
import { Debug } from "VSS/Diag";
import { WorkItemChangeEventArgs, ItemSource, AnnotationItemSource } from "Agile/Scripts/TFS.Agile.Boards";
import { GitHubLinksAnnotationItemSourceDataProviderResponse } from "TFS/WorkItemTracking/DataProviderContracts";
import {
    createGitHubArtifactFromExternalLinkedArtifactIdentifier,
    createGitHubArtifactFromExternalLink
} from "WorkItemTracking/Scripts/Utils/GitHubArtifactLink";
import { ExternalLink, WorkItem, Link } from "WorkItemTracking/Scripts/TFS.WorkItemTracking";
import { ToolNames } from "VSS/Artifacts/Constants";

const GitHubLinksAnnotationItemSourceDataProviderId = "ms.vss-work-web.github-links-annotation-item-source-data-provider";

export class GitHubLinksAnnotationItemSource extends AnnotationItemSource {
    public static sourceType: string = "githublinks";
    private linkedArtifacts: { [id: number]: ILinkedArtifact[] };

    constructor(parentSource: ItemSource, teamId: string) {
        super(parentSource, teamId);
        this.linkedArtifacts = {};
    }

    public type(): string {
        return GitHubLinksAnnotationItemSource.sourceType;
    }

    public getItem(id: number): ILinkedArtifact[] {
        return this.linkedArtifacts[id];
    }

    public getItems(ids: number[]) {
        Debug.fail("GitHubLinksAnnotationItemSource.getItems not implemented, not expected to be called.");
    }

    public async updateItem(payload: WorkItem): Promise<void> {
        if (!payload) {
            return;
        }

        const { id, allLinks, store } = payload;

        if (!store.contributedLinkTypes) {
            await store.beginGetContributedLinkTypes();
        }

        this.linkedArtifacts[id] = allLinks.reduce((links: ILinkedArtifact[], value: Link) => {
            if (value instanceof ExternalLink) {
                const link = createGitHubArtifactFromExternalLink(value);
                if (link.tool === ToolNames.GitHub) {
                    links.push(link);
                }
            }
            return links;
        }, []);
    }

    public async beginGetItem(id: number, args?: WorkItemChangeEventArgs): Promise<ILinkedArtifact[]> {
        await this.beginGetItems([id], args);
        return this.linkedArtifacts[id];
    }

    public async beginGetItems(ids: number[], args?: WorkItemChangeEventArgs): Promise<{ [id: number]: ILinkedArtifact[] }> {
        if (args && !args.externalLinkChanged) {
            return;
        }

        const pageDataService = getService(WebPageDataService);
        const githubLinksAnnotationItems = await pageDataService.getDataAsync<GitHubLinksAnnotationItemSourceDataProviderResponse>(
            GitHubLinksAnnotationItemSourceDataProviderId,
            undefined /** serviceInstanceId */,
            { workItemIds: ids }
        );
        const { links: githubLinkIdentifiersLookup } = githubLinksAnnotationItems;

        const workItemIdsWithGitHubAnnotation: number[] = [];
        for (const workItemId of ids) {
            const linkIdentifers = githubLinkIdentifiersLookup[workItemId];
            if (linkIdentifers) {
                workItemIdsWithGitHubAnnotation.push(workItemId);
                this.linkedArtifacts[workItemId] = linkIdentifers.map(identifier =>
                    createGitHubArtifactFromExternalLinkedArtifactIdentifier(identifier)
                );
            }
        }

        this.parentSource.raiseEventForAnnotationItemSource(workItemIdsWithGitHubAnnotation, this);
    }

    public dispose(): void {
        this.linkedArtifacts = null;
    }
}
